import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import {
  DynamicImageInput,
  EPrepImagesSource,
} from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useDraftResources_draftImage,
  useDraftResources_draftImageVariables,
} from './graphql-types/useDraftResources_draftImage';
import {
  useDraftResources_draftVideo,
  useDraftResources_draftVideoVariables,
} from './graphql-types/useDraftResources_draftVideo';

const DRAFT_IMAGE_QUERY = gql`
  query useDraftResources_draftImage(
    $input: GetDraftImageProxyInput!
    $imageInput: DynamicImageInput!
  ) {
    getDraftImageProxy(input: $input) {
      imageId
      imageProxy(input: $imageInput)
    }
  }
`;

const DRAFT_VIDEO_QUERY = gql`
  query useDraftResources_draftVideo($input: GetDraftVideoFileInput!) {
    getDraftVideoFiles(input: $input) {
      results {
        videoId
        videoFile {
          _id
          s3DownloadUrl
          hlsVideoStreamUrl
          thumbnailImageUrl(input: { height: 384 })
        }
      }
    }
  }
`;

type IResources = Array<
  | {
      imageId: string;
      url?: string;
    }
  | {
      videoId: string;
      url?: string;
    }
>;

type OriginalDescriptiveResources =
  | {
      videoId: string;
      videoFile: {
        _id: string;
        s3DownloadUrl: string | null;
        hlsVideoStreamUrl: string | null;
        thumbnailImageUrl: string | null;
      } | null;
    }
  | { imageId: string; imageProxy: string | null };

export const useDraftResources = (options: {
  originalDescriptiveResources: OriginalDescriptiveResources[];
  newDescriptiveResourcesIds: ({ imageId: string } | { videoId: string })[];
  source: EPrepImagesSource;
  imageInput?: DynamicImageInput;
}): {
  resourcesSyncedWithDraft: IResources;
} => {
  const [inProcessVideosIds, setInProcessVideosIds] = React.useState<string[]>(
    []
  );
  const [resourcesSyncedWithDraft, setResourcesSyncedWithDraft] =
    React.useState<IResources>([]);

  const { refetch } = useQuery<
    useDraftResources_draftImage,
    useDraftResources_draftImageVariables
  >(DRAFT_IMAGE_QUERY, { skip: true });

  const { data, error, startPolling, stopPolling } = useQuery<
    useDraftResources_draftVideo,
    useDraftResources_draftVideoVariables
  >(DRAFT_VIDEO_QUERY, {
    variables: {
      input: {
        videoIds: inProcessVideosIds,
      },
    },
    skip: !inProcessVideosIds.length,
  });

  React.useEffect(() => {
    const processImages = async (): Promise<void> => {
      try {
        /**
         * Sync up new resources with old
         */

        const result = options.newDescriptiveResourcesIds.map((newResource) => {
          let url;

          options.originalDescriptiveResources.forEach((resource) => {
            if ('imageId' in newResource && 'imageId' in resource) {
              if (newResource.imageId === resource.imageId) {
                url = resource.imageProxy ?? undefined;
              }
            } else if ('videoId' in newResource && 'videoId' in resource) {
              if (newResource.videoId === resource.videoId) {
                url = resource.videoFile?.thumbnailImageUrl ?? undefined;
              }
            }
          });

          return {
            ...newResource,
            url,
          };
        });

        /**
         * Sync new image resources with draft image data
         */
        const imagesWithoutData = options.newDescriptiveResourcesIds;

        const draftImagesData = await Promise.all(
          imagesWithoutData.map(async (image) => {
            if ('imageId' in image) {
              return await refetch({
                input: {
                  imageId: image.imageId,
                  source: options.source,
                },
                imageInput: options.imageInput
                  ? options.imageInput
                  : { fit: 'crop', ar: '4:3', h: 384 },
              });
            }

            return null;
          })
        );
        const draftImages = draftImagesData.map(
          (value) => value?.data?.getDraftImageProxy
        );

        const result1 = result.map((resource) => {
          if ('videoId' in resource) {
            return resource;
          }

          const match = draftImages.find(
            (image) => resource.imageId === image?.imageId
          );

          return {
            ...resource,
            url: match?.imageProxy ?? resource?.url,
          };
        });

        /**
         * Sync new video resources with draft video data
         */

        const result2 = result1.map((resource) => {
          if ('imageId' in resource) {
            return resource;
          }

          const match = data?.getDraftVideoFiles?.results.find(
            (video) => resource.videoId === video.videoId
          );

          return {
            ...resource,
            url: match?.videoFile?.thumbnailImageUrl ?? '',
          };
        });

        const videosWithoutDataIds = result2
          .filter((resource) => 'videoId' in resource && !resource.url)
          .map((video) => ('videoId' in video ? video?.videoId : ''));

        setResourcesSyncedWithDraft(result2);
        setInProcessVideosIds(videosWithoutDataIds);
      } catch (error) {
        logError(error);
      }
    };

    void processImages();
  }, [
    data?.getDraftVideoFiles?.results,
    options.imageInput,
    options.newDescriptiveResourcesIds,
    options.originalDescriptiveResources,
    options.source,
    refetch,
  ]);

  React.useEffect(() => {
    if (!error && inProcessVideosIds.length) {
      startPolling(10000);
    } else {
      stopPolling();
    }
  }, [inProcessVideosIds, error, startPolling, stopPolling]);

  return { resourcesSyncedWithDraft };
};
